import { useEffect, useMemo, useState } from "react";
import styles from "./variant.module.scss";
import { IconButton } from "@mui/material";
import { images } from "../../../../constants/images";
import { capitalize, formatInput } from "../../../../helpers/app";
import TextFieldShared from "../../../../components/TextField/TextField";
import SelectShared from "../../../../components/Select/Select";
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FileUploader from "../../../../components/FileUploader";
import { VariantNewSalePostProps } from "../../../../interfaces/components";
import { useToast } from "../../../../helpers/hooks";

const Variant = ({
    id,
    updateVariants,
    colors,
    onOpenModalColor,
    variants,
    postNewVariantFile,
    deleteNewVariantFile,
    onDelete,
    autoOpen = false
}: VariantNewSalePostProps) => {
    const toast = useToast();
    const [open, setOpen] = useState<boolean>(false);

    const onChangeColor = (value: string) => {
        const colorSelected = colorsNormalized && colorsNormalized.find((item: any) => item?.code === value)
        
        updateVariants({
            id: id,
            color: {...variant?.color, data: {name: colorSelected?.label, value: colorSelected?.code}}, 
            stock: variant?.stock,
            interiorFiles: variant?.interiorFiles,
            exteriorFiles: variant?.exteriorFiles,
            backgroundFile: variant?.backgroundFile
        });
    }
    
    const onChangeStock = (value: string) => {
        const { formattedNumber } = formatInput(value);
        updateVariants({
            id: id,
            color: variant?.color, 
            stock: {...variant?.color, value: formattedNumber},
            interiorFiles: variant?.interiorFiles,
            exteriorFiles: variant?.exteriorFiles,
            backgroundFile: variant?.backgroundFile
        });
    }
    
    const onChangeFileUploader = (files: any, type: string) => {
        let data = {
            id: id,
            color: variant?.color, 
            stock: variant?.stock,
            interiorFiles: variant?.interiorFiles,
            exteriorFiles: variant?.exteriorFiles,
            backgroundFile: variant?.backgroundFile
        };
        if(variant){
            if(type === "exterior"){
                data = {
                    ...data,
                    exteriorFiles: {...variant?.exteriorFiles, value: files}
                }
            }
            if(type === "interior"){
                data = {
                    ...data,
                    interiorFiles: {...variant?.interiorFiles, value: files}
                }
            }
            if(type === "background"){
                data = {
                    ...data,
                    backgroundFile: {...variant?.backgroundFile, value: files}
                }
            }
        }else{
            toast("Ha ocurrido un error");
            return;
        }
        updateVariants(data);
        files && files.forEach(async(file: any) => {
            if(file.toDelete){
                deleteNewVariantFile(file?.id, variant?.id, type);
                return;
            }
            if(file?.previewURL){
                let formData = new FormData();
                formData.append("file", file?.file, file?.name);
                if(type === "exterior"){
                    postNewVariantFile(formData, variant?.id, file?.id, type);
                }
                if(type === "interior"){
                    postNewVariantFile(formData, variant?.id, file?.id, type);
                }
                if(type === "background"){
                    postNewVariantFile(formData, variant?.id, file?.id, type);
                }
            }
        });
    }

    const variant: any = useMemo(() => {
        return variants && variants.find((variant: any) => variant.id === id)
    }, [variants]);
    
    const colorsNormalized: any = useMemo(() => {
        return colors && colors.map((color: any, index: number) => (
            {
                id: index,
                label: color?.label,
                name: <div className={styles.colorOption}>
                    <div className={styles.code} style={{background: `${color?.code}`}}></div>
                    <p>{`${capitalize(color?.label)}`}</p>
                </div>,
                code: color?.code,
                isSelected: variants && variants.some((variant: any) => color?.code === variant?.color?.data?.value),
                check: ""
            }
        ))
    }, [colors, variants]);

    useEffect(() => {
        let timer: any;
        if(autoOpen){
            timer = setTimeout(() => {
                setOpen(true);
            },500)
        }
        return () => {
            if(timer) clearTimeout(timer);
        }
    },[autoOpen])

    return (
        <div className={`${styles.variant} ${open && styles.open}`}>
            <div className={styles.header}>
                <KeyboardArrowDownIcon className={styles.add} onClick={() => setOpen(!open)}/>
                <p onClick={() => setOpen(!open)}>Nueva variante</p>
                <IconButton className={styles.delete} aria-label="Eliminar variante" onClick={() => onDelete(id)}>
                    <img src={images.trashBlue} alt="Eliminar" className={styles.icon} />
                </IconButton>
            </div>
            <div className={styles.content}>
                <div className={styles.color}>
                    <SelectShared
                        label="Color"
                        name="color"
                        value={variant?.color?.data?.value}
                        onChange={onChangeColor}
                        className={styles.selector}
                        options={colorsNormalized}
                        /* loading={colors?.fetching === "loading"} */
                        error={variant?.color?.error}
                        selectedOpacity
                        optionsEmptyMessage="No hay ningún color creado"
                        keyValue="code"
                    />
                    <IconButton className={styles.add} aria-label="Añadir" onClick={() => onOpenModalColor()}>
                        <AddIcon className={styles.icon} />
                    </IconButton>
                </div>
                <TextFieldShared
                    label="Stock"
                    name="stock"
                    value={variant?.stock?.value}
                    className={styles.half}
                    onChange={onChangeStock}
                    error={variant?.stock?.error}
                />
                <div className={`${styles.fileSelector} ${styles.exteriorFiles} ${styles.full}`}>
                    <p className={styles.title}>Fotos del exterior del vehículo (requerido)</p>
                    <FileUploader 
                        values={variant?.exteriorFiles?.value} 
                        onChange={(files: any) => onChangeFileUploader(files, "exterior")} 
                        className={styles.fileUploader} 
                        description="Sube tu imagen, con una resolución mínima de 1920x1080 pixeles en ambos lados y hasta 10 MB de peso."
                    />
                    {
                        variant?.exteriorFiles?.error && variant?.exteriorFiles?.error.length !== "" && 
                        <p className={styles.error}>{variant?.exteriorFiles?.error}</p>
                    }
                </div>
                <div className={`${styles.fileSelector} ${styles.interiorFiles} ${styles.full}`}>
                    <p className={styles.title}>Fotos del interior del vehículo (requerido)</p>
                    <FileUploader 
                        values={variant?.interiorFiles?.value} 
                        onChange={(files: any) => onChangeFileUploader(files, "interior")} 
                        className={styles.fileUploader} 
                        description="Sube tu imagen, con una resolución mínima de 1920x1080 pixeles en ambos lados y hasta 10 MB de peso."
                    />
                    {
                        variant?.interiorFiles?.error && variant?.interiorFiles?.error.length !== "" && 
                        <p className={styles.error}>{variant?.interiorFiles?.error}</p>
                    }
                </div>
                <div className={`${styles.fileSelector} ${styles.backgroundFile} ${styles.full}`}>
                    <p className={styles.title}>Foto de fondo (requerido)</p>
                    <p className={styles.description}>Esta foto es la que se verá en el formulario y en la compra, recordá que la foto debe poseer el vehículo en la izquierda y debe ser de formato horizontal.</p>
                    <FileUploader 
                        values={variant?.backgroundFile?.value} 
                        onChange={(files: any) => onChangeFileUploader(files, "background")} 
                        className={styles.fileUploader} 
                        description="Sube tu imagen, con una resolución mínima de 1920x1080 pixeles en ambos lados y hasta 10 MB de peso."
                        enumeration={false}
                        limit={1}
                    />
                    {
                        variant?.backgroundFile?.error && variant?.backgroundFile?.error.length !== "" && 
                        <p className={styles.error}>{variant?.backgroundFile?.error}</p>
                    }
                </div>
            </div>
        </div>
    )
};

export default Variant;